import React from 'react';
import { Link } from 'react-router-dom';
interface ButtonBaseProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
}
interface ButtonAsButton extends ButtonBaseProps {
  href?: undefined;
  to?: undefined;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
}
interface ButtonAsLink extends ButtonBaseProps {
  href: string;
  to?: undefined;
  onClick?: undefined;
  type?: undefined;
}
interface ButtonAsRouterLink extends ButtonBaseProps {
  to: string;
  href?: undefined;
  onClick?: undefined;
  type?: undefined;
}
type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsRouterLink;
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  className = '',
  ...rest
}: ButtonProps) {
  const baseStyles = 'font-semibold rounded-lg transition-all duration-200 inline-flex items-center justify-center hover:scale-[1.02] active:scale-[0.98]';
  const variantStyles = {
    primary: 'btn-gold-premium text-ink font-bold active:bg-yellow-600',
    secondary: 'border-2 border-accent text-accent hover:bg-accent hover:text-white shadow-sm hover:shadow-md',
    ghost: 'text-white hover:bg-white/10'
  };
  const sizeStyles = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };
  const disabledStyles = disabled ? 'opacity-50 cursor-not-allowed !hover:scale-100 !active:scale-100' : 'cursor-pointer';
  const combinedClassName = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${disabledStyles} ${className}`;
  // Internal route link
  if ('to' in rest && rest.to) {
    return <Link to={rest.to} className={combinedClassName} data-id="element-34">
        {children}
      </Link>;
  }
  // External or tel/mailto link
  if ('href' in rest && rest.href) {
    return <a href={rest.href} className={combinedClassName} data-id="element-35">
        {children}
      </a>;
  }
  // Regular button (form submits, etc.)
  const {
    onClick,
    type = 'button'
  } = rest as ButtonAsButton;
  return <button className={combinedClassName} onClick={onClick} type={type} disabled={disabled} data-id="element-36">
      {children}
    </button>;
}